/**
 * Typed access to Vite environment variables.
 *
 * Only `VITE_*` keys are exposed to the client bundle. All values here have
 * safe dev defaults so `npm run dev` works with no `.env` present.
 *
 * In staging/prod the real values are injected at build time by CI via
 * `VITE_*` env vars sourced from AWS Secrets Manager / Parameter Store.
 */

export type DeploymentEnv = 'dev' | 'staging' | 'prod' | 'test';

export interface QflyEnv {
  readonly appEnv: DeploymentEnv;
  readonly apiBaseUrl: string;
  readonly cognito: {
    readonly hostedUi: string;
    readonly clientId: string;
    readonly redirectUri: string;
  };
  readonly otel: {
    readonly endpoint: string;
    readonly serviceName: string;
    readonly serviceVersion: string;
  };
  readonly newRelic: {
    readonly licenseKey: string;
    readonly applicationId: string;
    readonly accountId: string;
    readonly trustKey: string;
  };
  readonly coralogixRum: {
    readonly publicKey: string;
    readonly domain: string;
  };
  readonly enableMocks: boolean;
}

const readStr = (key: string, fallback: string): string => {
  const value = import.meta.env[key];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
};

/** Read a boolean env var (`"true"`/`"false"`), falling back when unset. */
const readBool = (key: string, fallback: boolean): boolean => {
  const value = import.meta.env[key];
  if (typeof value === 'string' && value.length > 0) {
    return value.toLowerCase() === 'true' || value === '1';
  }
  return fallback;
};

const readEnv = (): DeploymentEnv => {
  const raw = readStr('VITE_APP_ENV', 'dev');
  // `.env.production` sets the literal string "production" (Vite's own mode
  // name passed via --mode, see cd-frontend.yml), not "prod" -- normalize it
  // so a real deployed prod build resolves to 'prod' instead of silently
  // falling through to the 'dev' default below. This was a live bug: today
  // isDev/isProd are consumed only by otel.ts's deployment.environment
  // label, but apiDocsAvailability.ts now also keys a dev-only Swagger UI
  // link off this value, making the fix security-adjacent, not cosmetic.
  if (raw === 'production') return 'prod';
  if (raw === 'dev' || raw === 'staging' || raw === 'prod' || raw === 'test') {
    return raw;
  }
  // Fails toward 'staging' (inert everywhere a dev-only surface checks for
  // 'dev'), not 'dev' -- an unrecognized/misspelled VITE_APP_ENV value must
  // never silently unlock a dev-only surface. Only the literal 'dev' (or an
  // entirely unset var, via readStr's own 'dev' fallback above -- the local
  // `npm run dev` case) does.
  return 'staging';
};

// Vite replaces `import.meta.env.PACKAGE_VERSION` via `define` in vite.config
// at build time; fallback to '0.0.0' when unavailable (e.g. Storybook).
const resolvedVersion = readStr('VITE_APP_VERSION', '0.1.0');

/**
 * Auto-derive the API base URL from the SPA's own hostname during the
 * staging→prod cutover: a single build serves both `dev.qfly.ceqhub.com`
 * (existing dev/test surface) and `qfly.ceqhub.com` (new prod alias
 * pointing at the same backend). The backend lives at `api.<host>`,
 * so we just look up the matching api domain. Falls through to
 * VITE_API_BASE_URL when the hostname doesn't match (local dev,
 * preview deploys, custom domains).
 */
const autoDetectApiBaseUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host === 'qfly.ceqhub.com') return 'https://api.qfly.ceqhub.com';
  if (host === 'dev.qfly.ceqhub.com') return 'https://api.dev.qfly.ceqhub.com';
  if (host === 'qfly.ceqstudio.com') return 'https://api.qfly.ceqstudio.com';
  return null;
};

export const env: QflyEnv = {
  appEnv: readEnv(),
  apiBaseUrl: autoDetectApiBaseUrl() ?? readStr('VITE_API_BASE_URL', '/api/v1'),
  cognito: {
    hostedUi: readStr(
      'VITE_COGNITO_HOSTED_UI',
      'https://dev-auth.qfly.cloudeq.com/oauth2/authorize',
    ),
    clientId: readStr('VITE_COGNITO_CLIENT_ID', 'local-dev-client'),
    redirectUri: readStr(
      'VITE_COGNITO_REDIRECT_URI',
      typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '/auth/callback',
    ),
  },
  otel: {
    // Empty default = OTel SDK no-ops (see setupWebTelemetry). Set
    // VITE_OTEL_ENDPOINT in `.env.<env>` to opt in for that env.
    endpoint: readStr('VITE_OTEL_ENDPOINT', ''),
    serviceName: 'qfly-frontend',
    serviceVersion: resolvedVersion,
  },
  newRelic: {
    // Empty licenseKey = Browser agent no-ops (see setupBrowserAgent), so
    // dev/test traffic never reports into New Relic under the same
    // application. Set all four VITE_NEW_RELIC_* vars in `.env.<env>` to
    // opt in for that env. Non-secret: a browser RUM license key is shipped
    // to every visitor's browser by design, same posture as VITE_LOGODEV_TOKEN.
    licenseKey: readStr('VITE_NEW_RELIC_LICENSE_KEY', ''),
    applicationId: readStr('VITE_NEW_RELIC_APPLICATION_ID', ''),
    accountId: readStr('VITE_NEW_RELIC_ACCOUNT_ID', ''),
    trustKey: readStr('VITE_NEW_RELIC_TRUST_KEY', ''),
  },
  coralogixRum: {
    // Empty publicKey/domain = agent no-ops (see setupCoralogixRum). Set both
    // VITE_CORALOGIX_RUM_* vars in `.env.<env>` to opt in for that env.
    publicKey: readStr('VITE_CORALOGIX_RUM_PUBLIC_KEY', ''),
    domain: readStr('VITE_CORALOGIX_RUM_DOMAIN', ''),
  },
  // MSW mocking. Defaults ON in dev/test (standalone UI, no backend needed),
  // but VITE_ENABLE_MOCKS now overrides it — set `VITE_ENABLE_MOCKS=false` in
  // .env.local to run the SPA against a REAL local backend (see the Vite proxy
  // in vite.config.ts + docs/LOCAL_DEV.md). Previously this flag was ignored.
  enableMocks: readBool(
    'VITE_ENABLE_MOCKS',
    import.meta.env.DEV || import.meta.env.MODE === 'test',
  ),
};

export const isDev = env.appEnv === 'dev';
export const isProd = env.appEnv === 'prod';
